import { useEffect, useState, FC, PropsWithChildren, useRef } from 'react';
import classNames from 'classnames';

import { useCollapsible } from '@hh.ru/magritte-common-use-collapsible';
import { AccordionProps } from '@hh.ru/magritte-ui-accordion/types';
import { Card } from '@hh.ru/magritte-ui-card';
import { ChevronDownOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';

import styles from './accordion.less';

const Accordion: FC<PropsWithChildren<AccordionProps>> = ({
    title,
    onClick,
    padding = 16,
    children,
    expanded = false,
    stretched = true,
    controlled = false,
    borderRadius = 12,
}) => {
    const [isLocalExpanded, setIsLocalExtended] = useState(expanded);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setIsLocalExtended(expanded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled]);

    const isExpanded = (controlled && expanded) || (!controlled && isLocalExpanded);
    const { collapsibleClasses } = useCollapsible(ref, isExpanded);

    const handleToggle = () => {
        if (!controlled) {
            setIsLocalExtended((prev) => !prev);
        }

        onClick?.();
    };
    const contentStyle = { padding: `0 ${padding}px ${padding}px ${padding}px` };

    return (
        <Card borderRadius={borderRadius} stretched={stretched}>
            <div className={styles.titleWrapper}>
                <Card actionCard borderRadius={borderRadius} padding={padding} onClick={handleToggle} stretched>
                    <div className={styles.header}>
                        <div data-qa="accordion-title" className={styles.title}>
                            {title}
                        </div>
                        <div
                            data-qa="accordion-chevron"
                            style={{ marginLeft: `${padding}px` }}
                            className={classNames(styles.titleIcon, {
                                [styles.titleIconOpen]: isExpanded,
                            })}
                        >
                            <ChevronDownOutlinedSize24 alignmentBaseline="auto" initial="contrast" />
                        </div>
                    </div>
                </Card>
            </div>
            <div ref={ref} className={collapsibleClasses} data-qa="collapsible">
                <div data-qa="accordion-content" style={contentStyle}>
                    {children}
                </div>
            </div>
        </Card>
    );
};

export default Accordion;
